import { styled } from "goober";
import { type ReactNode, useEffect } from "react";
import { Text } from "../Text";
import { useToast } from "../Toast";
import { SignInButton, SignInLayout } from "./shared";

const Actions = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
`;

const ERROR_MESSAGES: Record<string, string> = {
  "Failed to fetch":
    "Couldn't connect to the server. Check your internet connection and try again.",
  "Could not reach the authentication server":
    "Couldn't connect to the server. Check your internet connection and try again.",
  "SSO sign-in failed":
    "Single sign-on failed. Try again, or contact your admin.",
};

function friendlyError(raw: string): string {
  const mapped = ERROR_MESSAGES[raw];
  if (mapped) return mapped;
  if (/^Server returned \d+$/.test(raw))
    return "Something went wrong on our end. Try again in a moment.";
  return raw;
}

export function SignInErrorOrLogout({
  error,
  title,
  detail,
  onPrimaryAction,
  primaryActionLabel = "Sign in",
  secondaryAction,
}: {
  error: string | undefined;
  title?: string;
  detail?: ReactNode;
  onPrimaryAction: () => void;
  primaryActionLabel?: string;
  secondaryAction?: { label: string; onPress: () => void };
}) {
  const { showToast } = useToast();

  useEffect(() => {
    if (error) {
      showToast({
        icon: "traffic-cone",
        message: friendlyError(error),
        buttonLabel: "Dismiss",
        variant: "error",
        persist: true,
      });
    }
  }, [error, showToast]);

  return (
    <SignInLayout title={title}>
      {detail && (
        <Text variant="text-regular-md" color="var(--text-secondary)">
          {detail}
        </Text>
      )}
      <Actions>
        <SignInButton onPress={() => onPrimaryAction()}>
          <Text variant="text-regular-md">{primaryActionLabel}</Text>
        </SignInButton>
        {secondaryAction && (
          <SignInButton
            data-muted="true"
            onPress={() => secondaryAction.onPress()}
          >
            <Text variant="text-regular-md">{secondaryAction.label}</Text>
          </SignInButton>
        )}
      </Actions>
    </SignInLayout>
  );
}
